import React from 'react'
import {
  Collection,
  ForEachModel,
  ModelProxy,
  ModelWithForm,
  TriggerPipelineOnOperationExecute,
  WhenCaseIs,
  WhenModel,
  WhenModelValue
} from '@morningtrain/react-resources'
import Fieldset from 'layouts/form/Fieldset'
import * as Fields from 'support/fields'
import * as UI from 'ui'
import AdminPageWithSmartview from 'pages/app/AdminPageWithSmartview'
import SupplierCategoryButton from 'widgets/inventory/SupplierCategoryButton'
import { MountSmartview } from '@morningtrain/react-smartviews'
import SupplierCategoriesSmartview from 'widgets/smartviews/inventory/SupplierCategoriesSmartview'
import ChosenInventoryItems from 'widgets/smartviews/partials/ChosenInventoryItems'
import { observer } from 'mobx-react'
import { Repeater } from '@morningtrain/react-fields-repeater-field'
import { Select, Switch } from '@morningtrain/react-fields'
import { Translation, Translator } from '@morningtrain/react-translations'
import RedirectToEditOnCreatePipeline from 'support/pipelines/RedirectToEditOnCreatePipeline'
import * as Icons from 'ui/icons'
import {
  Link,
  NavigateOnPipe,
  OnNavigatePipeline,
  useRouter,
  WhenRouteHasParameter
} from '@morningtrain/react-navigation'
import { RouteParameterFilter } from '@morningtrain/react-filters'
import { NestedAsyncPipeline, Pipeline } from '@morningtrain/react-helpers'
import SupplierCategoriesToEnvOnPipe from 'widgets/inventory/SupplierCategoriesToEnvOnPipe'
import TranslatedTitle from 'widgets/TranslatedTitle'
import DeleteModelOnClick from 'support/actions/generic/DeleteModelOnClick'
import ModelValueTitle from 'widgets/ModelValueTitle'
import ImageUpload from 'support/fields/custom/ImageUpload'
import MountSmartviewWithModelOnClick from 'support/utilities/MountSmartviewWithModelOnClick'

function Create () {
  const router = useRouter()

  const isCreating = router.currentRoute === 'app.inventory.supplier.create'

  return (
    <AdminPageWithSmartview withGlobalDepartmentFilter={false}>
      <OnNavigatePipeline>
        <MountSmartview smartview={<SupplierCategoriesSmartview/>}/>
      </OnNavigatePipeline>
      <Translator namespace='inventory.supplier'>
        <ModelWithForm resourceName='inventory.supplier' autoFetch={false}>
          <WhenRouteHasParameter name='inventory_supplier'>
            <RouteParameterFilter name='inventory_supplier'/>
          </WhenRouteHasParameter>

          <Pipeline>
            <TriggerPipelineOnOperationExecute/>
            <NestedAsyncPipeline>
              <SupplierCategoriesToEnvOnPipe/>
            </NestedAsyncPipeline>
          </Pipeline>

          <WhenModel exists={!isCreating}>

            <UI.PageHeader title={isCreating ? <TranslatedTitle name='create_title'/> : <ModelValueTitle name='name'/>}>
              <UI.ButtonWrap>
                <WhenModelValue name='id'>
                  <Link route='app.inventory.supplier.departments' parameters={{ inventory_supplier: 'model:id' }}>
                    <UI.Button color='outlineDark'>
                      <UI.WithIcon size={5} placement='right' icon={<UI.Icons.Settings/>}>
                        <Translation name='actions.manage_departments'/>
                      </UI.WithIcon>
                    </UI.Button>
                  </Link>
                </WhenModelValue>
                <DeleteModelOnClick pipes={<NavigateOnPipe route='app.inventory.supplier.index'/>}>
                  <UI.SquareButton color='btn-red' big>
                    <Icons.Trash className='w-4 h-4'/>
                  </UI.SquareButton>
                </DeleteModelOnClick>
                <UI.Button type='submit'>
                  <Translation name='generic.save'/>
                </UI.Button>
              </UI.ButtonWrap>
            </UI.PageHeader>

            <UI.CardGroup>

              <UI.CardWithHeader header={<Translation name='basic_information.title'/>}>
                <Translator namespace='inventory.supplier.basic_information.fields'>
                  <Fieldset cols={2}>
                    <Fields.Input name="name" required/>
                    <Fields.TabsField name="department_area" enum="inventory_department_area" defaultValue="all"/>
                    <Fields.Input name="notification_email"/>
                    <Fields.Number name="default_days_count" required defaultValue={1}/>
                    <Fields.Time required name="order_before_time"/>
                    <Fields.Select
                            enum="weekday" multiple
                            name="delivery_days_array"
                    />
                    <ImageUpload name="image" fromName="image_url" label={<Translation name="fields.image.label"/>}/>
                  </Fieldset>
                </Translator>
              </UI.CardWithHeader>

              <UI.Card>
                <UI.CardHeader>
                  <UI.SecondaryHeading>
                    <Translation name='categories.title'/>
                  </UI.SecondaryHeading>
                  <MountSmartviewWithModelOnClick smartview={<SupplierCategoriesSmartview/>}>
                    <UI.SquareButton color='btn-primary'>
                      <Icons.Settings className='w-5 h-5'/>
                    </UI.SquareButton>
                  </MountSmartviewWithModelOnClick>
                </UI.CardHeader>
                <Collection resourceName='inventory.category'>
                  <UI.FlexGrid cols={3}>
                    <ForEachModel>
                      <ModelProxy>
                        <SupplierCategoryButton/>
                        <ChosenInventoryItems/>
                      </ModelProxy>
                    </ForEachModel>
                  </UI.FlexGrid>
                </Collection>
              </UI.Card>

              <UI.Card>
                <UI.CardHeader withMargin={false} className='-mb-12'>
                  <UI.SecondaryHeading>
                    <Translation name='deliverables.title'/>
                  </UI.SecondaryHeading>
                </UI.CardHeader>
                <Repeater
                  name='deliverables' draggable={false}
                  buttonClass='btn btn-sq btn-primary'
                  addLabel={<Icons.Plus className='w-3 h-3'/>}
                  removeLabel={<Icons.Trash className='w-4 h-4'/>}
                >
                  <Switch name='type' label='type' enum='inventory_order_deliverable_type' defaultValue='hoka_api'>
                    <WhenCaseIs value='hoka_api'>
                      <Select name='target' enum='hoka_api_target' label='Hoka Target'/>
                    </WhenCaseIs>
                    <WhenCaseIs value='pdf_email'>
                      <Fields.Input type='email' name='target' label='Email target'/>
                    </WhenCaseIs>
                    <WhenCaseIs value='gs_api'>
                      <Fields.Input type='email' name='target' label='Email target'/>
                    </WhenCaseIs>
                  </Switch>
                </Repeater>
              </UI.Card>
            </UI.CardGroup>
          </WhenModel>
          <RedirectToEditOnCreatePipeline routeName='app.inventory.supplier.edit'/>
        </ModelWithForm>
      </Translator>
    </AdminPageWithSmartview>
  )
}

export default observer(Create)
